/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'
import { faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { ReactNode, useEffect, useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, Row, Stack } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'
import type { Query } from '../../gql/graphql'
import FileUploader from '../FileUploader'

const validationSchema = yup.object().shape({
  title: yup.string()
    .min(1, 'O título deve ter no mínimo 1 caractere')
    .max(255, 'O título deve ter no máximo 255 caracteres'),
  url: yup.string()
    .url('A URL deve ser válida')
    .nullable('A URL não pode ser nula')
    .min(1, 'A URL deve ter no mínimo 1 caractere')
    .max(255, 'A URL deve ter no máximo 255 caracteres'),
  priority: yup.number().positive()
    .min(0, 'A prioridade deve ser um numero positivo')
    .max(20, 'A prioridade deve ser menor ou igual a 100')
    .default(0),
  location: yup.number().positive('A localização deve ser um número positivo'),
  file: yup.lazy(value =>
    typeof value === 'object'
      ? yup.object().shape({ url: yup.string(), mimetype: yup.string() })
      : yup.string()
  ),
  mobileFile: yup.lazy(value =>
    typeof value === 'object'
      ? yup.object().shape({ url: yup.string(), mimetype: yup.string() })
      : yup.string()
  ),
  status: yup.string()
    .oneOf(['active', 'inactive', 'deleted'], 'A situação deve ser ativo ou inativo')
    .default('inactive'),
  conditions: yup.object().shape({
    category: yup.mixed(),
    location: yup.mixed(),
    action: yup.mixed(),
    productSection: yup.mixed()
  }).nullable(),
  publishedIn: yup.string().default(() => moment().format('YYYY-MM-DDTHH:mm')),
  unpublishedIn: yup.string().default(() => moment().add(5, 'days').format('YYYY-MM-DDTHH:mm'))
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormBannerHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormBannerHandleSubmit
  initialValues?: Partial<ValidationSchema>
  isLoading?: boolean
  readOnly?: boolean
  customFooterMessage?: ReactNode
  bannerLocations?: Query['bannerLocations']
  productSections?: Query['productSections']
}

/**
 * FormBanner is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormBanner from './src/views/PostEdit/FormBanner.tsx'
 *
 * const MyComponent = () => (
 *   <FormBanner />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormBanner: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false,
  readOnly = false,
  customFooterMessage = null,
  bannerLocations = [],
  productSections = []
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  const resolution = useMemo(() => {
    return bannerLocations?.find(bannerLocation =>
      bannerLocation?.id === parseInt(formik?.values?.location as unknown as string)
    )
  }, [bannerLocations, formik])

  useEffect(() => {
    formik.setFieldValue('file', undefined)
    formik.setFieldValue('mobileFile', undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution])

  const selectedConditions = bannerLocations
    ?.find(location => location.id === parseInt(formik?.values?.location?.toString() ?? '', 10))
    ?.conditions ?? {}

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row style={{ paddingBottom: 20 }}>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Titulo do banner (máx.: 255 caracteres)" className="mb-3">
            <Form.Control
              type='text'
              name='title'
              maxLength={255}
              value={formik?.values?.title ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="URL (máx.: 255 caracteres)" className="mb-3">
            <Form.Control
              type='text'
              name='url'
              maxLength={255}
              value={formik?.values?.url ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <Form.Label>Local do banner</Form.Label>
          <Stack direction="horizontal" gap={2}>
            {bannerLocations?.filter(location => location.id === formik.values?.location).map(location => (
              <Col md={4}>
                <DropdownItem
                  isDisabled
                  elemBefore={(
                    <>
                      {location.preview !== null && (
                        <img
                          src={location?.preview}
                          width={80}
                          height={80}
                          alt='Imagem do banner'
                        />
                      )}
                    </>
                  )}
                  description={`Página ${location.page} (${location.width}x${location.height} px)`}
                >
                  {location.description}
                </DropdownItem>
              </Col>
            ))}
            <Col md={4}>
              <DropdownMenu
                shouldFitContainer
                trigger={({ triggerRef, ...triggerProps }) => (
                  <Button ref={triggerRef} {...triggerProps} size='sm'>
                    Selecione o tipo do banner
                  </Button>
                )}
              >
                <DropdownItemGroup>
                  {bannerLocations?.map((location, index) => (
                    <DropdownItem
                      onClick={() => formik.setFieldValue('location', location.id)}
                      elemBefore={(
                        <>
                          {location.preview !== null && (
                            <img
                              src={location?.preview}
                              width={80}
                              height={80}
                              alt='Imagem do banner'
                            />
                          )}
                        </>
                      )}
                      description={`Página ${location.page} (${location.width}x${location.height} px)`}
                    >
                      {location.description}
                    </DropdownItem>
                  ))}
                </DropdownItemGroup>
              </DropdownMenu>
            </Col>
          </Stack>
        </Form.Group>
        {isEqual(selectedConditions, { "action": "header", "location": "product-department" }) && (
          <Form.Group as={Col} md={12} className='mb-3'>
            <FloatingLabel label="Sessão associada ao banner" className="mb-3">
              <Form.Select
                value={formik.values?.conditions?.productSection ?? ''}
                // onChange={formik.handleChange}
                onChange={event => {
                  formik.setFieldValue('conditions', {
                    ...formik.values.conditions,
                    productSection: event.target.value
                  })
                }}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting || isLoading}
                name="productSection"
              >
                <option>Selecione uma opção</option>
                {productSections.map(section => (
                  <option value={section.id} key={section.id}>
                    {section.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>
        )}
        {isEqual(selectedConditions, { category: "offer-ads", location: "product-department" }) && (
          <Form.Group as={Col} md={12} className='mb-3'>
            <FloatingLabel label="Sessão associada ao banner" className="mb-3">
              <Form.Select
                value={formik.values?.conditions?.productSection ?? ''}
                // onChange={formik.handleChange}
                onChange={event => {
                  formik.setFieldValue('conditions', {
                    ...formik.values.conditions,
                    productSection: event.target.value
                  })
                }}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting || isLoading}
                name="productSection"
              >
                <option>Selecione uma opção</option>
                {productSections.map(section => (
                  <option value={section.id} key={section.id}>
                    {section.name}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Form.Group>
        )}
        <Form.Group as={Col} md={8} className='mb-3'>
          <FloatingLabel label="Visibilidade" className="mb-3">
            <Form.Select
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading}
              name="status"
            >
              <option>Selecione uma opção</option>
              <option value='active'>🌎 Pública</option>
              <option value='inactive'>🔒 Privada</option>
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={4} className='mb-3'>
          <FloatingLabel label="Prioridade" className="mb-3">
            <Form.Control
              type='number'
              name='priority'
              min={0}
              max={100}
              value={formik?.values?.priority ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={6} className='mb-3'>
          <FloatingLabel label="Ativar em" className="mb-3">
            <Form.Control
              type='datetime-local'
              name='publishedIn'
              value={formik?.values?.publishedIn ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={6} className='mb-3'>
          <FloatingLabel label="Inativar em" className="mb-3">
            <Form.Control
              type='datetime-local'
              name='unpublishedIn'
              value={formik?.values?.unpublishedIn ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        {formik.values.location && (
          <Row>
            {(resolution?.height !== null && resolution?.width !== null) && (
              <Form.Group as={Col} md={12} className='mb-3'>
                <Form.Label>Arquivo de imagem desktop (obrigatório {resolution?.width}x{resolution?.height}px)</Form.Label>
                <br />
                <FileUploader
                  variant='outline-primary'
                  accept='image/*'
                  size='sm'
                  resolutionHeight={resolution?.height ?? 0}
                  resolutionWidth={resolution?.width ?? 0}
                  resolutionType='absolute'
                  disabled={formik.isSubmitting || isLoading || readOnly || !resolution?.height || !resolution?.width}
                  onReceiveFiles={([{ dataURL }]) => {
                    formik.setFieldValue('file', dataURL)
                  }}
                  onError={error => console.error(error.message)}
                >
                  <FontAwesomeIcon icon={faUpload} />
                  {' '}
                  Enviar arquivo
                </FileUploader>
              </Form.Group>
            )}
            {(resolution?.mobileHeight !== null && resolution?.mobileWidth !== null) && (
              <Form.Group as={Col} md={12} className='mb-3'>
                <Form.Label>Arquivo de imagem mobile (obrigatório {resolution?.mobileWidth}x{resolution?.mobileHeight}px)</Form.Label>
                <br />
                <FileUploader
                  variant='outline-primary'
                  accept='image/*'
                  size='sm'
                  resolutionHeight={resolution?.mobileHeight ?? 0}
                  resolutionWidth={resolution?.mobileWidth ?? 0}
                  resolutionType='absolute'
                  disabled={formik.isSubmitting || isLoading || readOnly || !resolution?.mobileHeight || !resolution?.mobileWidth}
                  onReceiveFiles={([{ dataURL }]) => formik.setFieldValue('mobileFile', dataURL)}
                  onError={error => console.error(error.message)}
                >
                  <FontAwesomeIcon icon={faUpload} />
                  {' '}
                  Enviar arquivo
                </FileUploader>
              </Form.Group>
            )}
          </Row>
        )}
        <Col md={12}>
          <Button
            variant='outline-link'
            style={{ marginRight: 10 }}
            onClick={() => navigate('/banners')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type='submit'
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
          {' '}
          {customFooterMessage}
        </Col>
      </Row>
    </Form>
  )
}

export default FormBanner
